import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Modal,
  Paper,
  Radio,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { searchEndCucstomerAccountView } from "API/swesum";
import MUITable from "Components/UI/Tables/MUITable";
import useTableOptions from "Hooks/useTableOptions";
import { forwardRef, useEffect, useState } from "react";
import { price2StringPrice } from "utils/priceformatter";
import { getInvoiceTypeChip, getSystemStatusLabelById } from "utils/labels";
import CloseIcon from "@mui/icons-material/Close";
import useDebounce from "Hooks/useDebounce";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import { getSystemStatusColorByLabel } from "utils/colors";
import {
  DEFAULT_SEARCH_FIELD_KEYS,
  MIN_SEARCH_LENGTH,
  SEARCH_FIELDS,
} from "utils/accountSearchFields";

const INTEREST_PAY_RULE = "RÄNTEFAKTURA";

const CHIP_BORDER = "#b9bfc9";
const CHIP_TEXT = "#3d434e";
const CHIP_HOVER_FILL = "#f2f4f8";
const CARD_BORDER = "#dfe3ea";
const CARD_SHADOW = "0 1px 2px rgba(16,24,40,.06)";
const LABEL_TEXT = "#5b6270";

const CHIP_HEIGHT = 32;

const srOnly = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const SearchFieldChip = forwardRef(({ label, selected, onToggle, ...other }, ref) => (
  <ButtonBase
    ref={ref}
    role="checkbox"
    aria-checked={selected}
    onClick={onToggle}
    focusRipple
    {...other}
    sx={(theme) => ({
      height: CHIP_HEIGHT,
      minHeight: CHIP_HEIGHT,
      px: "12px",
      gap: "6px",
      borderRadius: `${CHIP_HEIGHT / 2}px`,
      border: "1px solid",
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1,
      textTransform: "none",
      transition: theme.transitions.create([
        "background-color",
        "border-color",
        "color",
      ]),
      borderColor: selected ? theme.palette.primary.main : CHIP_BORDER,
      backgroundColor: selected ? theme.palette.primary.main : "transparent",
      color: selected
        ? theme.palette.primary.contrastText
        : CHIP_TEXT,
      "&:hover": {
        borderColor: theme.palette.primary.main,
        backgroundColor: selected
          ? theme.palette.primary.dark
          : CHIP_HOVER_FILL,
      },
      "&.Mui-focusVisible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: "2px",
      },
    })}
  >
    {selected ? <CheckIcon aria-hidden sx={{ fontSize: 14 }} /> : null}
    {label}
  </ButtonBase>
));

// Avslutade fakturor ska inte gå att välja, med undantag för räntefakturor
// som kan vara avslutade men ändå ha belopp kvar att koppla.
const isRowSelectable = (value) => {
  if (value.estimatedRemainingAmount === 0) {
    return false;
  }

  const isActive = value.status === "active" || value.status === "paused";
  const isInterestWithAmountLeft =
    value.payRule === INTEREST_PAY_RULE && Number(value.amountLeftToPay) > 0;

  return isActive || isInterestWithAmountLeft;
};

const createRows = (invoices) => {
  return invoices.map((i) => {
    return [
      {
        id: i.InvoiceID,
        status: i.InvoiceState,
        estimatedRemainingAmount: i.Amount,
        payRule: i.PayRule,
        amountLeftToPay: i.AmountLeftToPay,
      },

      i.PayRule,
      { invoiceNumber: i.PrintInvoiceReference, id: i.InvoiceID },
      i.PrintOCR,
      { customerNumber: i.CustomerNumber, id: i.EndCustomerId },
      { customerName: i.CustomerName, id: i.EndCustomerId },
      { clientName: i.ClientName, id: i.ClientId },
      price2StringPrice(i.Amount ? i.Amount : 0),
      price2StringPrice(i.AmountLeftToPay ? i.AmountLeftToPay : 0),
      dayjs(i.BookingDate).format("YYYY-MM-DD"),
      dayjs(i.CalcFfdDate).format("YYYY-MM-DD"),
      getSystemStatusLabelById(i.SystemStatus),
    ];
  });
};

const StyledTable = styled(MUITable)(({ theme }) => ({
  margin: "2rem",
  [theme.breakpoints.down("lg")]: {
    margin: "1rem",
  },
  border: "none",
  boxShadow: "none",
  display: "flex",
  flexDirection: "column",
  flex: 1,
  ".MuiTable-root": {
    marginTop: "auto",
    ".MuiTableCell-root": {
      "&:nth-of-type(1)": {
        paddingTop: "0",
        paddingBottom: "0",
      },
    },
  },
}));

const SearchModal = ({
  open,
  onClose,
  onSelect,
  initialSearchTerm,
  isMatchingView = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [pastedTerm, setPastedTerm] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500, pastedTerm);
  const [inputNode, setInputNode] = useState(null);
  const [searchFields, setSearchFields] = useState(DEFAULT_SEARCH_FIELD_KEYS);

  const { handleColumnShow, isColDisplayed } = useTableOptions(
    "EndCustomerSearchTable"
  );

  const trimmedTerm = (debouncedSearchTerm ?? "").trim();
  const hasFields = searchFields.length > 0;
  const termTooShort =
    trimmedTerm.length > 0 && trimmedTerm.length < MIN_SEARCH_LENGTH;

  // En inklistrad term är redan färdigskriven och söks direkt i stället för
  // att vänta ut debouncen.
  const onSearchTermChange = (e) => {
    setPastedTerm(e.nativeEvent.inputType === "insertFromPaste");
    setSearchTerm(e.target.value);
  };

  const toggleField = (key) =>
    setSearchFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  useEffect(() => {
    inputNode?.focus();
  }, [inputNode]);

  useEffect(() => {
    setSearchTerm(initialSearchTerm);
  }, [initialSearchTerm]);

  useEffect(() => {
    const abortController = new AbortController();

    const performSearch = async () => {
      setSelectedRowId(null);

      // Varje kolumn söks med LIKE '%term%', så korta termer och en tom
      // kolumnvalslista är inte värda en förfrågan.
      if (trimmedTerm.length < MIN_SEARCH_LENGTH || !hasFields) {
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const res = await searchEndCucstomerAccountView(
          trimmedTerm,
          searchFields,
          abortController.signal
        );

        if (res.status === 200) {
          const rows = createRows(res.data);
          setRows(rows);
        }
      } catch (err) {
        if (
          err.name === "AbortError" ||
          err.name === "CanceledError" ||
          abortController.signal.aborted
        ) {
          return;
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    performSearch();

    return () => {
      abortController.abort();
    };
  }, [trimmedTerm, searchFields, hasFields]);

  const onSelectClick = () => {
    if (selectedRowId) {
      onSelect(selectedRowId);
    }
  };

  const scrollAreaStyle = {
    position: "absolute",
    inset: 0,
    display: "flex",
    overflow: "auto",
    outline: "none",
    p: { xs: 1, md: 2, lg: 3 },
  };

  const stackStyle = {
    margin: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    width: "100%",
    maxWidth: "125rem",
  };

  const searchCardStyle = {
    width: "100%",
    maxWidth: "60rem",
    background: "white",
    padding: { xs: "16px", md: "20px" },
    borderRadius: "8px",
    border: `1px solid ${CARD_BORDER}`,
    boxShadow: CARD_SHADOW,
  };

  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    width: "100%",
    minHeight: "min(46rem, calc(100vh - 19rem))",
    borderRadius: "0.5em",
    boxShadow:
      "0px 5px 6px -3px rgba(0,0,0,0.2), 0px 9px 12px 1px rgba(0,0,0,0.14)",
  };

  const columns = [
    {
      name: "Välj",
      options: {
        display: isMatchingView,
        filter: false,
        customBodyRender: (value) => {
          return (
            <Radio
              checked={
                selectedRowId !== null
                  ? Number(value.id) === Number(selectedRowId)
                  : false
              }
              disabled={!isRowSelectable(value)}
              onChange={() => setSelectedRowId(value.id)}
            />
          );
        },
      },
    },
    {
      name: "Typ",
      options: {
        display: isColDisplayed("Typ"),
        filter: false,
        customBodyRender: (value) => {
          const chip = getInvoiceTypeChip(value);
          return (
            <Chip
              label={value}
              sx={{ background: chip.color, color: "white" }}
            />
          );
        },
      },
    },
    {
      name: "Fakturanr",
      options: {
        display: isColDisplayed("Fakturanr"),
        filter: false,
        customBodyRender: (value) => {
          return (
            <Link
              to={`/invoices/${value.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {value.invoiceNumber}
            </Link>
          );
        },
      },
    },
    {
      name: "OCR",
      options: {
        display: isColDisplayed("OCR"),
        filter: false,
      },
    },
    {
      name: "Kundnr",
      options: {
        display: isColDisplayed("Kundnr"),
        filter: false,
        customBodyRender: (value) => {
          return (
            <Link
              to={`/endcustomers/${value.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {value.customerNumber}
            </Link>
          );
        },
      },
    },
    {
      name: "Namn",
      options: {
        display: isColDisplayed("Namn"),
        filter: false,
        customBodyRender: (value) => {
          return (
            <Link
              to={`/endcustomers/${value.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {value.customerName}
            </Link>
          );
        },
      },
    },
    {
      name: "Klient",
      options: {
        display: isColDisplayed("Klient"),
        filter: false,
        customBodyRender: (value) => {
          return (
            <Link
              to={`/clients/${value.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {value.clientName}
            </Link>
          );
        },
      },
    },
    {
      name: "Amount",
      label: "Ursprungligt belopp",
      options: {
        display: isColDisplayed("Amount"),
        filter: false,
      },
    },
    {
      name: "AmountLeft",
      label: "Kvar att koppla",
      options: {
        display: isMatchingView,
      },
    },
    {
      name: "BookingDate",
      label: "Bokföringsdatum",
    },
    {
      name: "CalcFfdDate",
      label: "Förfallodatum",
      options: {
        display: isMatchingView,
      },
    },
    {
      name: "SystemStatus",
      options: {
        customBodyRender: (value) => {
          return (
            <Chip
              label={value}
              color={getSystemStatusColorByLabel(value)}
              sx={{ minWidth: "6rem" }}
            />
          );
        },
      },
    },
  ];

  const options = {
    selectableRowsHeader: true,
    selectableRowsHideCheckboxes: true,
    rowsPerPageOptions: [10],
    rowsPerPage: 10,
    elevation: 0,
    textLabels: {
      body: {
        noMatch: loading ? <CircularProgress /> : "",
      },
    },
    onViewColumnsChange: (changedColumn, action) => {
      handleColumnShow(changedColumn, action);
    },
    setRowProps: (row, dataIndex, rowIndex) => {
      if (!isMatchingView) return;

      const currentRow = rows[dataIndex];

      const isDisabled = !isRowSelectable(currentRow[0]);

      const isSelected =
        selectedRowId !== null &&
        Number(currentRow[0].id) === Number(selectedRowId);

      return {
        style: {
          backgroundColor: isDisabled
            ? "#f5f5f5"
            : isSelected
            ? "#e3f2fd"
            : "inherit",
          opacity: isDisabled ? 0.75 : 1,
          cursor: isDisabled ? "not-allowed" : "pointer",
        },
      };
    },
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
          },
        },
      }}
    >
      <Box sx={scrollAreaStyle}>
        <Box sx={stackStyle}>
          {/* Sökkortet ligger i flödet ovanför modalen i stället för att hänga
              utanför dess överkant. Kolumnvalen kan fortfarande fällas ut utan
              att lägga sig över tabellen, men kortet kan aldrig hamna utanför
              rutan när modalen är lika hög som fönstret. */}
          <Box sx={searchCardStyle}>
            <TextField
              value={searchTerm ?? ""}
              inputRef={setInputNode}
              onChange={onSearchTermChange}
              label="Sök"
              InputLabelProps={{ shrink: true }}
              helperText={
                !hasFields
                  ? "Välj minst ett sökfält"
                  : termTooShort
                  ? `Skriv minst ${MIN_SEARCH_LENGTH} tecken`
                  : " "
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ m: 0 }}>
                    <SearchIcon sx={{ fontSize: 18, color: LABEL_TEXT }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ m: 0 }}>
                    {loading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <CheckCircleIcon
                        color="success"
                        sx={{ fontSize: 18 }}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={(theme) => ({
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  height: 48,
                  px: "14px",
                  gap: "12px",
                  borderRadius: "6px",
                  fontSize: 16,
                  fontWeight: 400,
                  "& fieldset": { borderColor: CHIP_BORDER },
                  "&:hover fieldset": { borderColor: CHIP_BORDER },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                    borderWidth: "2px",
                  },
                },
                "& .MuiOutlinedInput-input": { padding: 0 },
                "& .MuiInputLabel-root": {
                  color: LABEL_TEXT,
                  "&.Mui-focused": { color: theme.palette.primary.main },
                },
                "& .MuiFormHelperText-root": {
                  fontSize: 12,
                  color: LABEL_TEXT,
                  marginLeft: 0,
                },
              })}
            />

            <Box
              component="fieldset"
              sx={{
                border: 0,
                margin: 0,
                padding: 0,
                minWidth: 0,
                marginTop: "4px",
              }}
            >
              <Box component="legend" sx={srOnly}>
                Sök på
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                }}
              >
                <Typography
                  aria-hidden
                  sx={{
                    flexShrink: 0,
                    fontSize: 13,
                    fontWeight: 500,
                    color: LABEL_TEXT,
                    lineHeight: `${CHIP_HEIGHT}px`,
                  }}
                >
                  Sök på
                </Typography>
                <Box
                  sx={{
                    minWidth: 0,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {SEARCH_FIELDS.map((field) => (
                    <SearchFieldChip
                      key={field.key}
                      label={field.label}
                      selected={searchFields.includes(field.key)}
                      onToggle={() => toggleField(field.key)}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          <Paper sx={containerStyle}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                flex: 1,
              }}
            >
              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
              <StyledTable
                title={"Fakturor"}
                options={options}
                data={rows}
                columns={columns}
              />
              {isMatchingView ? (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <LoadingButton
                    variant="contained"
                    onClick={onSelectClick}
                    disabled={selectedRowId === null}
                    sx={{
                      width: "10rem",
                      marginRight: "2rem",
                      marginBottom: "2rem",
                    }}
                  >
                    Välj
                  </LoadingButton>
                </div>
              ) : null}
            </div>
          </Paper>
        </Box>
      </Box>
    </Modal>
  );
};

export default SearchModal;
